import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  ColDef,
  ColGroupDef,
  ColumnApi,
  ColumnGroupOpenedEvent,
  ColumnGroupShowType,
  GridApi,
  GridOptions,
  GetContextMenuItemsParams,
  IAggFuncParams,
  MenuItemDef,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { addDays, format, isAfter, isBefore, isSameDay, parseISO } from 'date-fns';
import { HOURS } from 'components/common/AllocationReport/consts';
import { useReportState } from 'components/common/DrilldownReports/hooks/useReportState';
import { useReportRowGroups } from 'components/common/DrilldownReports/hooks/useReportRowGroups';
import { createValueFormatter } from 'components/common/DrilldownReports/utils/valueFormatters';
import {
  calculateLoadTime,
  getNetworkSpeed,
  logReportPerformance,
} from 'components/common/DrilldownReports/utils/reportUtils';
import {
  AvailabilityDisplayFrame,
  AvailabilityDisplayWeek,
  AvailabilityGranularity,
  AvailabilityReportData,
  AvailabilityReportFilter,
  AvailabilityReportTableParams,
  TransformedAvailabilityRequest,
  UseAvailabilityReport,
} from 'components/pages/AvailabilityReport/AvailabilityReport.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { TableHeader } from 'components/common/TableHeader';
import { HeaderBackgroundColor, TextColor } from 'components/common/TableHeader/TableHeader.types';
import { Store } from 'components/common/types/Store.types';
import { HeaderGroupComponent } from 'components/common/AllocationReport/Table/CustomComponents/HeaderGroupComponent';
import {
  generateTimeBreakdownColumnHeaders,
  redrawGroupedRowsWhenSortChanged,
  togglePinnedColumnOnColumnGroupOpen,
} from 'components/common/AllocationReport/Table/utils';
import RowGroupRenderer from 'components/common/Table/RowGroupRenderer';
import { useRowGroupColumns } from 'customHooks/useRowGroupColumns';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { getDirectSalesAvailabilityReport } from 'modules/api/availabilityReport';
import { AVAILABILITY_PERCENT_EXCEL_STYLE, getAvailabilityDataCellClass, ROW_GROUP_COLUMNS } from './consts';
import { FrameIdFilter, FrameIdFloatingFilter } from './FrameIdFilter';
import { transformAvailabilityRequestPayload } from './transformAvailabilityRequestPayload';
import {
  calculateAvailabilityWeekStartDate,
  compareAvailabilityFrameColumn,
  convertToNumeric,
  getAvailabilityExportFileName,
  getAvailabilityExportGroupLabel,
  getAvailabilityTotal,
  getAvailabilityValues,
  getRangeValues,
  getTotalValue,
  shouldSkipCollapsedAvailabilityExportRow,
  transformAvailabilityToDisplayFormat,
} from './utils';
import { useExportAvailabilityReport } from './useExportAvailabilityReport';

const availabilityExportMenuIcon = (iconName: string): string =>
  `<span class="ag-icon ag-icon-${iconName}" unselectable="on" role="presentation"></span>`;

const getAvailabilityContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] =>
  (params.defaultItems ?? []).map((item) => {
    if (item !== 'export') {
      return item;
    }

    return {
      name: 'Export',
      icon: availabilityExportMenuIcon('save'),
      subMenu: [
        {
          name: 'CSV Export',
          icon: availabilityExportMenuIcon('csv'),
          action: () => {
            params.api.exportDataAsCsv({
              fileName: getAvailabilityExportFileName('availability-report', 'csv'),
            });
          },
        },
        {
          name: 'Excel Export',
          icon: availabilityExportMenuIcon('excel'),
          action: () => {
            params.api.exportDataAsExcel({
              fileName: getAvailabilityExportFileName('availability-report', 'xlsx'),
            });
          },
        },
      ],
    };
  });

const defaultPayload = (
  selectedMetricType: string,
): { granularity: AvailabilityGranularity; mode: string; metric: string } => ({
  granularity: AvailabilityGranularity.WEEK,
  mode: 'AVAILABILITY',
  metric: selectedMetricType,
});

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useAvailabilityReport: UseAvailabilityReport = ({ onDataFetched }) => {
  const rowGroupColumns = useRowGroupColumns(
    ROW_GROUP_COLUMNS,
    (state: Store) => state.publisher.configuration.availabilityReportDefaultRowGroups,
  );

  const {
    isLoading,
    startOfLoadingTimeInMs,
    columnApi,
    marketId,
    localeCode,
    campaignType,
    setIsLoading,
    setStartOfLoadingTimeInMs,
    setColumnApi,
    selectedMetricType,
    handleChangeMetricType,
  } = useReportState('availability');

  const { selectedRowGroupColumns, handleToggleRowGroupColumn, resetRowGroupColumns, updateRowGroupColumnState } =
    useReportRowGroups(rowGroupColumns, columnApi);

  const [availabilityReportData, setAvailabilityReportData] = useState<AvailabilityReportData>();
  const [rowData, setRowData] = useState<AvailabilityDisplayFrame[]>([]);
  const [totalAvailability, setTotalAvailability] = useState<AvailabilityDisplayWeek[]>([]);
  const [expandingWeeks, setExpandingWeeks] = useState<Set<number>>(new Set());
  const [expandingDays, setExpandingDays] = useState<Set<string>>(new Set());
  const [currentFilters, setCurrentFilters] = useState<AvailabilityReportFilter | null>(null);
  const [openColumnGroups, setOpenColumnGroups] = useState<Set<string>>(new Set());
  const [frameIdFilterActive, setFrameIdFilterActive] = useState(false);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [isRestoringGroups, setIsRestoringGroups] = useState(false);
  const [isRefetchingGranularData, setIsRefetchingGranularData] = useState(false);
  const [isRefetchingMainData, setIsRefetchingMainData] = useState(false);
  const isRefetchingGranularDataRef = useRef(false);
  const isRefetchingMainDataRef = useRef(false);
  const granularRefetchTimeoutRef = useRef<number | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const needsGranularRefetchRef = useRef(false);
  const updatePinnedBottomRowRef = useRef<(() => void) | undefined>();
  const needsPinnedRowUpdateRef = useRef(false);
  const isTableLoadingRef = useRef(false);
  const openColumnGroupsRef = useRef<Set<string>>(new Set());
  const savedFrameIdFilterRef = useRef<{ filterType: string; values: string[] } | null>(null);

  useEffect(() => {
    openColumnGroupsRef.current = openColumnGroups;
  }, [openColumnGroups]);

  const restoreColumnGroups = useCallback(
    (apiToUse: ColumnApi) => {
      if (openColumnGroups.size === 0 || isRestoringGroups || !apiToUse) return;

      try {
        if (typeof apiToUse.setColumnGroupOpened !== 'function') {
          return;
        }

        setIsRestoringGroups(true);

        if (openColumnGroups.has('total-duration')) {
          apiToUse.setColumnGroupOpened('total-duration', true);
        }

        const hasWeekOrDayGroups = Array.from(openColumnGroups).some(
          (id) => id.startsWith('week-') || id.startsWith('day-'),
        );
        if (hasWeekOrDayGroups) {
          apiToUse.setColumnGroupOpened('total-duration', true);
        }

        openColumnGroups.forEach((groupId) => {
          apiToUse.setColumnGroupOpened(groupId, true);
        });
      } catch (error) {
        setColumnApi(null);
      } finally {
        setIsRestoringGroups(false);
      }
    },
    [openColumnGroups, isRestoringGroups],
  );

  const fetchAvailabilityReport = async (
    data: TransformedAvailabilityRequest,
    preserveExpandedState = false,
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setStartOfLoadingTimeInMs(performance.now());

      const payload = {
        ...data,
        ...defaultPayload(selectedMetricType),
      };

      const availabilityReportResponse = await getDirectSalesAvailabilityReport(marketId, payload, cancelFunctions);

      const availabilityData = availabilityReportResponse;

      setAvailabilityReportData(availabilityData);

      const displayFrames = transformAvailabilityToDisplayFormat(availabilityData, selectedMetricType);

      setRowData(displayFrames);

      if (displayFrames.length > 0) {
        if (
          availabilityData.metadata.granularity === AvailabilityGranularity.WEEK &&
          availabilityData.frames.length > 0
        ) {
          const maxWeeks = Math.max(
            ...availabilityData.frames.map(
              (frame) => getAvailabilityValues(frame.availability, selectedMetricType).length,
            ),
          );

          const availability = Array.from({ length: maxWeeks }, (_, weekIndex) => {
            const rangeValues = getRangeValues(availabilityData.summary, selectedMetricType);
            const weekTotal = convertToNumeric(rangeValues[weekIndex] || 0);

            const originalStartDate = parseISO(availabilityData.metadata.startDate);
            const weekStartDate = calculateAvailabilityWeekStartDate(originalStartDate, weekIndex);

            return {
              availabilityWeek: weekIndex,
              total: weekTotal,
              days: [
                {
                  date: format(weekStartDate, 'yyyy-MM-dd'),
                  total: weekTotal,
                },
              ],
            };
          });

          if (preserveExpandedState) {
            setTotalAvailability((prevTotalAvailability) => {
              return availability.map((newWeek, weekIndex) => {
                const existingWeek = prevTotalAvailability.find((w) => w.availabilityWeek === weekIndex);
                const weekGroupId = `week-${weekIndex}`;
                if (existingWeek && openColumnGroups.has(weekGroupId)) {
                  return {
                    ...newWeek,
                    days:
                      existingWeek.days?.map((existingDay, dayIndex) => {
                        const newDay = newWeek.days?.[dayIndex];
                        const dayGroupId = `week-${weekIndex}-day-${existingDay.date}`;
                        if (newDay && openColumnGroups.has(dayGroupId)) {
                          return {
                            ...newDay,
                            hours: existingDay.hours,
                          };
                        }
                        return newDay || existingDay;
                      }) || newWeek.days,
                  };
                }
                return newWeek;
              });
            });
          } else {
            setTotalAvailability(availability);
          }
        } else if (
          availabilityData.metadata.granularity === AvailabilityGranularity.HOUR &&
          availabilityData.frames.length > 0
        ) {
          const maxHours = Math.max(
            ...availabilityData.frames.map(
              (frame) => getAvailabilityValues(frame.availability, selectedMetricType).length,
            ),
          );

          const totalAvailabilityWeek: AvailabilityDisplayWeek = {
            availabilityWeek: 0,
            total: getTotalValue(availabilityData.summary, selectedMetricType),
            days: [
              {
                date: format(parseISO(availabilityData.metadata.startDate), 'yyyy-MM-dd'),
                total: getTotalValue(availabilityData.summary, selectedMetricType),
                hours: Array.from({ length: maxHours }, (_, hourIndex) => {
                  const rangeValues = getRangeValues(availabilityData.summary, selectedMetricType);
                  const hourTotal = convertToNumeric(rangeValues[hourIndex] || 0);
                  return {
                    hour: hourIndex,
                    total: hourTotal,
                  };
                }),
              },
            ],
          };
          setTotalAvailability([totalAvailabilityWeek]);
        } else {
          const totalAvailabilityWeek: AvailabilityDisplayWeek = {
            availabilityWeek: 0,
            total: getTotalValue(availabilityData.summary, selectedMetricType),
            days: [],
          };
          setTotalAvailability([totalAvailabilityWeek]);
        }
      }

      onDataFetched?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Availability Report fetch failed:', error);
      datadogLogs.logger.error('Availability Report fetch failed', {
        tradingType: campaignType,
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDailyData = useCallback(
    async (weekNumber: number, data: TransformedAvailabilityRequest, skipDuplicateCheck = false): Promise<void> => {
      if (!skipDuplicateCheck) {
        let isAlreadyExpanding = false;
        setExpandingWeeks((prev) => {
          if (prev.has(weekNumber)) {
            isAlreadyExpanding = true;
            return prev;
          }
          return new Set(prev).add(weekNumber);
        });

        if (isAlreadyExpanding) {
          return;
        }
      }

      try {
        const payload = {
          ...data,
          ...defaultPayload(selectedMetricType),
          granularity: AvailabilityGranularity.DAY,
          parent: { weekIndex: weekNumber },
        };

        const dailyData = await getDirectSalesAvailabilityReport(marketId, payload, cancelFunctions);

        if (!dailyData || !dailyData.frames || !dailyData.metadata) {
          return;
        }

        const dailyDisplayFrames = transformAvailabilityToDisplayFormat(dailyData, selectedMetricType, weekNumber);

        setRowData((prevData) => {
          return prevData.map((frame) => {
            const dailyFrame = dailyDisplayFrames.find((f) => f.frameId === frame.frameId);
            if (dailyFrame && dailyFrame.availability[0]?.days) {
              return {
                ...frame,
                availability: frame.availability.map((availability) => {
                  if (availability.availabilityWeek === weekNumber) {
                    const newDays = dailyFrame.availability[0].days;
                    const existingDays = availability.days;

                    const mergedDays =
                      newDays?.map((newDay, dayIndex) => {
                        const existingDay = existingDays?.[dayIndex];
                        return {
                          ...newDay,
                          hours: existingDay?.hours || newDay.hours,
                        };
                      }) || newDays;

                    return {
                      ...availability,
                      days: mergedDays,
                    };
                  }
                  return availability;
                }),
              };
            }
            return frame;
          });
        });

        setTotalAvailability((prevTotals) => {
          return prevTotals.map((totalAvailabilityWeek) => {
            if (totalAvailabilityWeek.availabilityWeek === weekNumber && dailyData.frames.length > 0) {
              const maxDays = Math.max(
                ...dailyData.frames.map(
                  (frame) => getAvailabilityValues(frame.availability, selectedMetricType).length,
                ),
              );

              const originalStartDate = parseISO(dailyData.metadata.startDate);
              const weekStartDate = calculateAvailabilityWeekStartDate(originalStartDate, weekNumber);

              const dailyTotals = Array.from({ length: maxDays }, (_, dayIndex) => {
                const rangeValues = getRangeValues(dailyData.summary, selectedMetricType);
                const dayTotal = convertToNumeric(rangeValues[dayIndex] || 0);
                const existingDay = totalAvailabilityWeek.days?.[dayIndex];

                return {
                  date: format(addDays(weekStartDate, dayIndex), 'yyyy-MM-dd'),
                  total: dayTotal,
                  hours: existingDay?.hours,
                };
              });

              return {
                ...totalAvailabilityWeek,
                days: dailyTotals,
              };
            }

            return totalAvailabilityWeek;
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch daily availability data:', error);
      } finally {
        if (!skipDuplicateCheck) {
          setExpandingWeeks((prev) => {
            const newSet = new Set(prev);
            newSet.delete(weekNumber);
            return newSet;
          });
        }
      }
    },
    [selectedMetricType],
  );

  const fetchHourlyData = useCallback(
    async (
      weekNumber: number,
      dayDate: string,
      data: TransformedAvailabilityRequest,
      skipDuplicateCheck = false,
    ): Promise<void> => {
      const dayKey = `${weekNumber}-${dayDate}`;

      if (!skipDuplicateCheck) {
        let isAlreadyExpanding = false;
        setExpandingDays((prev) => {
          if (prev.has(dayKey)) {
            isAlreadyExpanding = true;
            return prev;
          }
          return new Set(prev).add(dayKey);
        });

        if (isAlreadyExpanding) {
          return;
        }
      }

      try {
        const dayIndex = (parseISO(dayDate).getDay() + 6) % 7;

        const payload = {
          ...data,
          ...defaultPayload(selectedMetricType),
          granularity: AvailabilityGranularity.HOUR,
          parent: { weekIndex: weekNumber, dayIndex },
        };

        const hourlyData = await getDirectSalesAvailabilityReport(marketId, payload, cancelFunctions);

        if (!hourlyData || !hourlyData.frames || !hourlyData.metadata) {
          return;
        }

        const maxHours = Math.max(
          ...hourlyData.frames.map((frame) => getAvailabilityValues(frame.availability, selectedMetricType).length),
        );

        const rangeValues = getRangeValues(hourlyData.summary, selectedMetricType);
        const hourlyTotals = Array.from({ length: maxHours }, (_, hourIndex) => ({
          hour: hourIndex,
          total: convertToNumeric(rangeValues[hourIndex] || 0),
        }));

        setRowData((prevData) => {
          return prevData.map((frame) => {
            const hourlyFrameData = hourlyData.frames.find((f) => f.frameId === frame.frameId);
            if (hourlyFrameData) {
              const hourlyValues = getAvailabilityValues(hourlyFrameData.availability, selectedMetricType).map(
                (value, hourIndex) => ({
                  hour: hourIndex,
                  total: convertToNumeric(value),
                }),
              );

              return {
                ...frame,
                availability: frame.availability.map((availability) =>
                  availability.availabilityWeek === weekNumber
                    ? {
                        ...availability,
                        days:
                          availability.days?.map((day) =>
                            day.date === dayDate
                              ? {
                                  ...day,
                                  hours: hourlyValues,
                                }
                              : day,
                          ) || availability.days,
                      }
                    : availability,
                ),
              };
            }
            return frame;
          });
        });

        setTotalAvailability((prevTotals) => {
          return prevTotals.map((totalAvailabilityWeek) => {
            if (totalAvailabilityWeek.availabilityWeek === weekNumber) {
              return {
                ...totalAvailabilityWeek,
                days:
                  totalAvailabilityWeek.days?.map((day) =>
                    day.date === dayDate
                      ? {
                          ...day,
                          hours: hourlyTotals,
                        }
                      : day,
                  ) || totalAvailabilityWeek.days,
              };
            }
            return totalAvailabilityWeek;
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch hourly availability data:', error);
      } finally {
        if (!skipDuplicateCheck) {
          setExpandingDays((prev) => {
            const newSet = new Set(prev);
            newSet.delete(dayKey);
            return newSet;
          });
        }
      }
    },
    [selectedMetricType],
  );

  const updateSelectedRowGroupColumns = (colApi: ColumnApi): void => {
    const activeRowGroupColumnsIds = colApi.getRowGroupColumns().map((rowGroupColumn) => rowGroupColumn.getColId());

    const updatedRowGroupColumns = rowGroupColumns.map((rowGroupColumn) => {
      const isSelected = activeRowGroupColumnsIds.includes(rowGroupColumn.colId);

      return {
        ...rowGroupColumn,
        isSelected,
        groupOrder: isSelected ? activeRowGroupColumnsIds.indexOf(rowGroupColumn.colId) : undefined,
      };
    });

    handleToggleRowGroupColumn(updatedRowGroupColumns.filter((col) => col.isSelected));
  };

  const createBasePayload = useCallback((): TransformedAvailabilityRequest | null => {
    if (!currentFilters) {
      return null;
    }

    const transformedData = transformAvailabilityRequestPayload(currentFilters, marketId);
    if (!transformedData) {
      return null;
    }

    return { ...transformedData, ...defaultPayload(selectedMetricType) };
  }, [currentFilters, selectedMetricType, marketId]);

  const refetchExpandedGranularityData = useCallback(async (): Promise<void> => {
    if (isRefetchingGranularDataRef.current) {
      return;
    }

    if (!gridApi) {
      return;
    }

    const basePayload = createBasePayload();
    if (!basePayload) {
      return;
    }

    const expandedWeeks = new Set<number>();
    const expandedDays = new Set<string>();

    const currentOpenGroups = openColumnGroupsRef.current;
    currentOpenGroups.forEach((groupId) => {
      const weekMatch = groupId.match(/^week-(\d+)$/);
      if (weekMatch) {
        expandedWeeks.add(parseInt(weekMatch[1]));
      }

      const dayMatch = groupId.match(/^week-(\d+)-day-(.+)$/);
      if (dayMatch) {
        const [weekNumber, dayDate] = dayMatch.slice(1);
        expandedDays.add(`${weekNumber}-${dayDate}`);
        expandedWeeks.add(parseInt(weekNumber));
      }
    });

    setIsRefetchingGranularData(true);
    isRefetchingGranularDataRef.current = true;

    try {
      const processWeeksSequentially = async (weeks: number[]): Promise<void> => {
        if (weeks.length === 0) {
          return;
        }

        const [weekNumber, ...remainingWeeks] = weeks;

        const dailyResult = await fetchDailyData(weekNumber, basePayload, true).catch(() => null);

        await new Promise((resolve) => {
          queueMicrotask(() => resolve(undefined));
        });

        if (dailyResult !== null && currentFilters?.mediaType === DirectSalesMediaType.DIGITAL) {
          const weekDays = Array.from(expandedDays).filter((dayKey) => {
            const dashIndex = dayKey.indexOf('-');
            const dayWeekNumber = parseInt(dayKey.substring(0, dashIndex));
            return dayWeekNumber === weekNumber;
          });

          if (weekDays.length > 0) {
            const hourlyPromises = weekDays.map((dayKey) => {
              const dashIndex = dayKey.indexOf('-');
              const dayDate = dayKey.substring(dashIndex + 1);
              return fetchHourlyData(weekNumber, dayDate, basePayload, true);
            });

            await Promise.allSettled(hourlyPromises);

            await new Promise((resolve) => {
              queueMicrotask(() => resolve(undefined));
            });
          }
        }

        await processWeeksSequentially(remainingWeeks);
      };

      await processWeeksSequentially(Array.from(expandedWeeks));
    } finally {
      setIsRefetchingGranularData(false);
      isRefetchingGranularDataRef.current = false;
      needsPinnedRowUpdateRef.current = true;
    }
  }, [gridApi, currentFilters?.mediaType, createBasePayload, fetchDailyData, fetchHourlyData]);

  useEffect(() => {
    if (needsPinnedRowUpdateRef.current && !isRefetchingGranularData && gridApi && totalAvailability.length > 0) {
      needsPinnedRowUpdateRef.current = false;

      requestAnimationFrame(() => {
        if (typeof gridApi.refreshCells === 'function') {
          gridApi.refreshCells({ force: true });
        }
        updatePinnedBottomRowRef.current?.();
      });
    }
  }, [isRefetchingGranularData, gridApi, totalAvailability]);

  const handleFormatValue = (params: ValueFormatterParams): string => {
    if (params.value == null || params.value === '') {
      return '-';
    }

    return createValueFormatter(selectedMetricType, localeCode)(params);
  };

  const aggFunc = useCallback(
    (params: IAggFuncParams): number => {
      const length = params?.values?.length ?? 0;

      if (!length) return 0;
      const sum = params.values.reduce((acc, curr) => acc + (curr || 0), 0);

      return selectedMetricType === MetricType.SOT ? sum / length : sum;
    },
    [selectedMetricType],
  );

  const dataCellClass = getAvailabilityDataCellClass(selectedMetricType);

  const createFrameIdColumn = (): ColDef => ({
    cellClass: 'frame-cell',
    cellClassRules: {
      'show-line': (params: { node: { firstChild: boolean } }) => !params.node.firstChild,
    },
    colId: 'frame-id',
    field: 'frameId',
    headerName: 'Frame ID',
    comparator: compareAvailabilityFrameColumn,
    headerComponent: TableHeader,
    headerComponentParams: {
      backgroundColor: HeaderBackgroundColor.WHITE,
      label: 'Frame ID',
      sortable: true,
      textColor: TextColor.SECONDARY,
    },
    cellRenderer: RowGroupRenderer,
    width: 230,
    pinned: 'left',
    sortable: true,
    showRowGroup: true,
    filter: FrameIdFilter,
    floatingFilter: true,
    floatingFilterComponent: FrameIdFloatingFilter,
    suppressMenu: false,
  });

  const createTotalAvailableColumn = (): ColDef => ({
    aggFunc,
    cellClass: dataCellClass,
    colId: 'frame-total',
    field: 'total',
    headerName: 'Total Available',
    headerComponent: TableHeader,
    headerComponentParams: {
      backgroundColor: HeaderBackgroundColor.WHITE,
      label: 'Total Available',
      textColor: TextColor.SECONDARY,
    },
    width: 170,
    valueGetter: (params: ValueGetterParams) => params.data?.total,
    valueFormatter: handleFormatValue,
  });

  const createRowGroupColumns = (): ColDef[] =>
    rowGroupColumns.map(({ colId, field, name }) => ({
      colId,
      field,
      headerValueGetter: () => name,
      hide: true,
      sortable: true,
    }));

  const createHourColumns = (
    availabilityWeekIndex: number,
    dayIndex: number,
    aggregationFunc: string | ((params: IAggFuncParams) => number),
  ): ColDef[] =>
    HOURS.map((hour, hourIndex) => ({
      aggFunc: aggregationFunc,
      cellClass: dataCellClass,
      columnGroupShow: 'open' as ColumnGroupShowType,
      field: `availability.${availabilityWeekIndex}.days.${dayIndex}.hours.${hourIndex}`,
      headerName: hour.label,
      headerComponent: TableHeader,
      headerComponentParams: {
        backgroundColor: HeaderBackgroundColor.PRIMARY,
        label: hour.label,
        headerLevel: 'Hour',
      },
      valueGetter: (params: ValueGetterParams) => {
        const availability = params.data?.availability?.find(
          (avail: AvailabilityDisplayWeek) => avail.availabilityWeek === availabilityWeekIndex,
        );
        return availability?.days?.[dayIndex]?.hours?.[hourIndex]?.total || 0;
      },
      valueFormatter: handleFormatValue,
    }));

  const createDayColumns = (
    week: { days: Array<{ date: Date; label: string }> },
    availabilityWeekIndex: number,
    aggregationFunc: string | ((params: IAggFuncParams) => number),
    reportData: AvailabilityReportData,
    expandingDaysSet: Set<string>,
  ): ColGroupDef[] =>
    week.days.map((day: { date: Date; label: string }, dayIndex: number) => ({
      aggFunc: aggregationFunc,
      columnGroupShow: 'open' as ColumnGroupShowType,
      groupId: `week-${availabilityWeekIndex}-day-${format(day.date, 'yyyy-MM-dd')}`,
      field: `availability.${availabilityWeekIndex}.days.${dayIndex}.total`,
      headerName: day.label,
      headerGroupComponent: HeaderGroupComponent,
      headerGroupComponentParams: {
        label: day.label,
        headerLevel: reportData.metadata.granularity === AvailabilityGranularity.DAY ? 'Day' : 'Time Period',
        isLoading: expandingDaysSet.has(`${availabilityWeekIndex}-${format(day.date, 'yyyy-MM-dd')}`),
      },
      valueGetter: (params: ValueGetterParams) => getAvailabilityTotal(params, availabilityWeekIndex, dayIndex),
      valueFormatter: handleFormatValue,
      children:
        currentFilters?.mediaType === DirectSalesMediaType.DIGITAL
          ? [
              {
                aggFunc: aggregationFunc,
                cellClass: dataCellClass,
                columnGroupShow: 'closed' as ColumnGroupShowType,
                field: `availability.${availabilityWeekIndex}.days.${dayIndex}.total`,
                headerName: '',
                valueGetter: (params: ValueGetterParams) =>
                  getAvailabilityTotal(params, availabilityWeekIndex, dayIndex),
                valueFormatter: handleFormatValue,
              },
              {
                aggFunc: aggregationFunc,
                cellClass: dataCellClass,
                columnGroupShow: 'open' as ColumnGroupShowType,
                field: `availability.${availabilityWeekIndex}.days.${dayIndex}.total`,
                headerName: 'Total',
                headerComponent: TableHeader,
                headerComponentParams: {
                  backgroundColor: HeaderBackgroundColor.SECONDARY,
                  label: 'Total',
                },
                valueGetter: (params: ValueGetterParams) =>
                  getAvailabilityTotal(params, availabilityWeekIndex, dayIndex),
                valueFormatter: handleFormatValue,
              },
              ...createHourColumns(availabilityWeekIndex, dayIndex, aggregationFunc),
            ]
          : [
              {
                aggFunc: aggregationFunc,
                cellClass: dataCellClass,
                columnGroupShow: 'closed' as ColumnGroupShowType,
                field: `availability.${availabilityWeekIndex}.days.${dayIndex}.total`,
                headerName: '',
                valueGetter: (params: ValueGetterParams) =>
                  getAvailabilityTotal(params, availabilityWeekIndex, dayIndex),
                valueFormatter: handleFormatValue,
              },
            ],
    }));

  const createWeeklyColumns = (
    timeBreakdownColumnHeaders: {
      weeks: Array<{ weekIndex: number; label: string; days: Array<{ date: Date; label: string }> }>;
    },
    aggregationFunc: string | ((params: IAggFuncParams) => number),
    reportData: AvailabilityReportData,
    expandingWeeksSet: Set<number>,
    expandingDaysSet: Set<string>,
  ): ColGroupDef[] =>
    timeBreakdownColumnHeaders.weeks.map(
      ({
        weekIndex: availabilityWeekIndex,
        ...week
      }: {
        weekIndex: number;
        label: string;
        days: Array<{ date: Date; label: string }>;
      }) => ({
        aggFunc: aggregationFunc,
        columnGroupShow: 'open' as ColumnGroupShowType,
        groupId: `week-${availabilityWeekIndex}`,
        headerName: week.label,
        headerGroupComponent: HeaderGroupComponent,
        headerGroupComponentParams: {
          label: week.label,
          headerLevel: reportData.metadata.granularity === AvailabilityGranularity.WEEK ? 'Time Slot' : 'Period',
          isLoading: expandingWeeksSet.has(availabilityWeekIndex),
        },
        children: [
          {
            aggFunc: aggregationFunc,
            cellClass: dataCellClass,
            columnGroupShow: 'closed',
            field: `availability.${availabilityWeekIndex}.total`,
            headerName: '',
            valueGetter: (params: ValueGetterParams) => getAvailabilityTotal(params, availabilityWeekIndex),
            valueFormatter: handleFormatValue,
          },
          {
            aggFunc: aggregationFunc,
            cellClass: dataCellClass,
            columnGroupShow: 'open' as ColumnGroupShowType,
            field: `availability.${availabilityWeekIndex}.total`,
            headerName: 'Total',
            headerGroupComponent: TableHeader,
            headerGroupComponentParams: {
              backgroundColor: HeaderBackgroundColor.SECONDARY,
              label: 'Total',
            },
            valueGetter: (params: ValueGetterParams) => getAvailabilityTotal(params, availabilityWeekIndex),
            valueFormatter: handleFormatValue,
            children: [
              {
                aggFunc: aggregationFunc,
                cellClass: dataCellClass,
                columnGroupShow: 'open' as ColumnGroupShowType,
                headerName: '',
                field: `availability.${availabilityWeekIndex}.total`,
                valueGetter: (params: ValueGetterParams) => getAvailabilityTotal(params, availabilityWeekIndex),
                valueFormatter: handleFormatValue,
              },
            ],
          },
          ...createDayColumns(week, availabilityWeekIndex, aggregationFunc, reportData, expandingDaysSet),
        ],
      }),
    );

  const timeBreakdownColumnHeaders = useMemo(() => {
    if (availabilityReportData?.metadata?.startDate && availabilityReportData?.metadata?.endDate && currentFilters) {
      const startDate = currentFilters.startDate
        ? new Date(currentFilters.startDate)
        : parseISO(availabilityReportData.metadata.startDate);
      const endDate = currentFilters.endDate
        ? new Date(currentFilters.endDate)
        : parseISO(availabilityReportData.metadata.endDate);

      const columnHeaders = generateTimeBreakdownColumnHeaders(startDate, endDate, localeCode);

      return {
        ...columnHeaders,
        weeks: columnHeaders.weeks
          .map((week) => ({
            ...week,
            days: week.days.filter(
              (day) =>
                (isAfter(day.date, startDate) || isSameDay(day.date, startDate)) &&
                (isBefore(day.date, endDate) || isSameDay(day.date, endDate)),
            ),
          }))
          .filter((week) => week.days.length > 0),
      };
    }

    return null;
  }, [
    availabilityReportData?.metadata?.startDate,
    availabilityReportData?.metadata?.endDate,
    currentFilters?.startDate,
    currentFilters?.endDate,
    localeCode,
  ]);

  const columnDefinitions = useMemo((): (ColDef | ColGroupDef)[] => {
    if (!availabilityReportData || !timeBreakdownColumnHeaders) {
      return [];
    }

    return [
      createFrameIdColumn(),
      {
        groupId: 'total-duration',
        headerName: timeBreakdownColumnHeaders.label,
        headerGroupComponent: HeaderGroupComponent,
        headerGroupComponentParams: {
          backgroundColor: HeaderBackgroundColor.WHITE,
          label: timeBreakdownColumnHeaders.label,
          isPinnedDuplicateHidden: true,
          headerLevel: 'Total availability',
        },
        openByDefault: false,
        children: [
          createTotalAvailableColumn(),
          ...createRowGroupColumns(),
          ...createWeeklyColumns(
            timeBreakdownColumnHeaders,
            aggFunc,
            availabilityReportData,
            expandingWeeks,
            expandingDays,
          ),
        ],
      },
    ];
  }, [availabilityReportData, timeBreakdownColumnHeaders, expandingWeeks, expandingDays, aggFunc, selectedMetricType]);

  const deferredUpdatePinnedRow = (): void => {
    queueMicrotask(() => {
      updatePinnedBottomRowRef.current?.();
    });
  };

  const onGridReady = useCallback<AvailabilityReportTableParams['onGridReady']>(
    (onGridReadyEvent) => {
      if (!availabilityReportData || !timeBreakdownColumnHeaders) {
        datadogLogs.logger.error('Availability Report load failed', { tradingType: campaignType });
        onGridReadyEvent.api.showNoRowsOverlay();
        return;
      }
      setColumnApi(onGridReadyEvent.columnApi);
      setGridApi(onGridReadyEvent.api);
      gridApiRef.current = onGridReadyEvent.api;
      updateRowGroupColumnState(onGridReadyEvent.columnApi, selectedRowGroupColumns);

      restoreColumnGroups(onGridReadyEvent.columnApi);

      if (savedFrameIdFilterRef.current) {
        const filterModel = savedFrameIdFilterRef.current;
        setFrameIdFilterActive(true);
        onGridReadyEvent.api.setFilterModel({ 'frame-id': filterModel });
        savedFrameIdFilterRef.current = null;
        deferredUpdatePinnedRow();
      }

      const loadTime = calculateLoadTime(startOfLoadingTimeInMs);
      logReportPerformance('Availability', campaignType, loadTime, getNetworkSpeed());
    },
    [availabilityReportData, timeBreakdownColumnHeaders, restoreColumnGroups, deferredUpdatePinnedRow],
  );

  updatePinnedBottomRowRef.current = () => {
    if (!gridApi || !totalAvailability.length || !availabilityReportData) {
      return;
    }

    const filterModel = gridApi.getFilterModel()?.['frame-id'];
    const isActive = !!(filterModel && filterModel.values && filterModel.values.length > 0);

    setFrameIdFilterActive(isActive);

    if (isActive) {
      const visibleRows: AvailabilityDisplayFrame[] = [];
      gridApi.forEachNodeAfterFilter((node) => {
        if (node.data && !node.group) {
          visibleRows.push(node.data);
        }
      });

      if (visibleRows.length === 0) {
        gridApi.setPinnedBottomRowData([
          {
            availability: totalAvailability,
            frameId: 'Total Available',
            total: 0,
          },
        ]);
        return;
      }

      const filteredTotal = visibleRows.reduce((sum, row) => sum + (row.total || 0), 0);

      const filteredAvailability = totalAvailability.map((week, weekIndex) => {
        const weekTotal = visibleRows.reduce((sum, row) => {
          const weekData = row.availability?.find((a) => a.availabilityWeek === weekIndex);
          return sum + (weekData?.total || 0);
        }, 0);

        const days = week.days?.map((day, dayIndex) => {
          const dayTotal = visibleRows.reduce((sum, row) => {
            const weekData = row.availability?.find((a) => a.availabilityWeek === weekIndex);
            const dayData = weekData?.days?.[dayIndex];
            return sum + (dayData?.total || 0);
          }, 0);

          const hours = day.hours?.map((hour, hourIndex) => {
            const hourTotal = visibleRows.reduce((sum, row) => {
              const weekData = row.availability?.find((a) => a.availabilityWeek === weekIndex);
              const dayData = weekData?.days?.[dayIndex];
              const hourData = dayData?.hours?.[hourIndex];
              return sum + (hourData?.total || 0);
            }, 0);

            return { ...hour, total: hourTotal };
          });

          return { ...day, total: dayTotal, hours };
        });

        return { ...week, total: weekTotal, days };
      });

      gridApi.setPinnedBottomRowData([
        {
          availability: filteredAvailability,
          frameId: 'Total Available',
          total: filteredTotal,
        },
      ]);

      const pinnedBottomNode = gridApi.getPinnedBottomRow(0);
      if (pinnedBottomNode) {
        gridApi.refreshCells({ rowNodes: [pinnedBottomNode], force: true });
      }
    } else {
      const backendTotal = getTotalValue(availabilityReportData.summary, selectedMetricType);
      gridApi.setPinnedBottomRowData([
        {
          availability: totalAvailability,
          frameId: 'Total Available',
          total: backendTotal,
        },
      ]);
      const pinnedBottomNode = gridApi.getPinnedBottomRow(0);
      if (pinnedBottomNode) {
        gridApi.refreshCells({ rowNodes: [pinnedBottomNode], force: true });
      }
    }
  };

  const isTableLoading =
    isLoading || isRefetchingGranularData || isRefetchingMainData || expandingWeeks.size > 0 || expandingDays.size > 0;

  isTableLoadingRef.current = isTableLoading;

  const onColumnGroupOpened = useCallback(
    (params: ColumnGroupOpenedEvent) => {
      const groupId = params.columnGroup.getGroupId();
      const isExpanding = params.columnGroup.isExpanded();

      if (groupId === 'total-duration' || groupId?.includes('total-duration')) {
        togglePinnedColumnOnColumnGroupOpen(params, 'total-duration', 'frame-total', 'right');

        setOpenColumnGroups((prev) => {
          const newSet = new Set(prev);
          if (isExpanding) {
            if (!newSet.has('total-duration')) {
              newSet.add('total-duration');
            }
          } else {
            newSet.delete('total-duration');
            Array.from(newSet).forEach((id) => {
              if (id.startsWith('week-') || id.startsWith('day-')) {
                newSet.delete(id);
              }
            });
            setExpandingWeeks(new Set());
            setExpandingDays(new Set());
          }
          return newSet;
        });

        deferredUpdatePinnedRow();
        return;
      }

      if (groupId && (groupId.startsWith('week-') || groupId.startsWith('day-'))) {
        setOpenColumnGroups((prev) => {
          const newSet = new Set(prev);
          if (isExpanding) {
            if (!newSet.has(groupId)) {
              newSet.add(groupId);
            }
          } else {
            const isDayGroup = groupId.includes('-day-');
            if (isDayGroup) {
              const weekMatch = groupId.match(/^week-(\d+)-day-/);
              if (weekMatch) {
                const parentWeekId = `week-${weekMatch[1]}`;
                if (newSet.has(parentWeekId)) {
                  newSet.delete(groupId);
                }
              }
            } else {
              newSet.delete(groupId);
            }
          }
          return newSet;
        });
      }

      if (isExpanding) {
        if (isRefetchingGranularDataRef.current || isRefetchingMainDataRef.current) {
          return;
        }

        const weekMatch = groupId?.match(/^week-(\d+)$/);
        if (weekMatch) {
          const weekNumber = parseInt(weekMatch[1]);
          const basePayload = createBasePayload();
          if (basePayload) {
            fetchDailyData(weekNumber, basePayload)
              .then(() => {
                if (currentFilters?.mediaType === DirectSalesMediaType.DIGITAL) {
                  const currentOpenGroups = openColumnGroupsRef.current;
                  const openDaysForThisWeek = Array.from(currentOpenGroups).filter((id) =>
                    id.startsWith(`week-${weekNumber}-day-`),
                  );

                  if (openDaysForThisWeek.length > 0) {
                    const hourlyPromises = openDaysForThisWeek.map((dayGroupId) => {
                      const dayMatch = dayGroupId.match(/^week-(\d+)-day-(.+)$/);
                      if (dayMatch) {
                        const dayDate = dayMatch[2];
                        return fetchHourlyData(weekNumber, dayDate, basePayload);
                      }
                      return Promise.resolve();
                    });

                    return Promise.all(hourlyPromises);
                  }
                }
                return undefined;
              })
              .then(deferredUpdatePinnedRow);
          }
          return;
        }

        const dayMatch = groupId?.match(/^week-(\d+)-day-(.+)$/);
        if (dayMatch && currentFilters?.mediaType === DirectSalesMediaType.DIGITAL) {
          const weekNumber = parseInt(dayMatch[1]);
          const dayDate = dayMatch[2];
          const basePayload = createBasePayload();
          if (basePayload) {
            const hasDailyData = rowData.some((frame) =>
              frame.availability?.some(
                (avail) => avail.availabilityWeek === weekNumber && avail.days && avail.days.length > 0,
              ),
            );

            if (!hasDailyData) {
              fetchDailyData(weekNumber, basePayload)
                .then(() => fetchHourlyData(weekNumber, dayDate, basePayload))
                .then(deferredUpdatePinnedRow);
            } else {
              fetchHourlyData(weekNumber, dayDate, basePayload).then(deferredUpdatePinnedRow);
            }
          }
        }
      } else {
        deferredUpdatePinnedRow();
      }
    },
    [
      currentFilters,
      createBasePayload,
      fetchDailyData,
      fetchHourlyData,
      rowData,
      deferredUpdatePinnedRow,
      selectedMetricType,
    ],
  );

  const gridOptions: GridOptions = {
    columnDefs: columnDefinitions,
    onGridReady,
    onFilterChanged: deferredUpdatePinnedRow,
    onColumnGroupOpened,
    excelStyles: [AVAILABILITY_PERCENT_EXCEL_STYLE],
    defaultCsvExportParams: {
      shouldRowBeSkipped: shouldSkipCollapsedAvailabilityExportRow,
      processRowGroupCallback: getAvailabilityExportGroupLabel,
    },
    defaultExcelExportParams: {
      shouldRowBeSkipped: shouldSkipCollapsedAvailabilityExportRow,
      processRowGroupCallback: getAvailabilityExportGroupLabel,
    },
    getContextMenuItems: getAvailabilityContextMenuItems,
    defaultColDef: {
      width: 120,
      resizable: true,
      suppressMenu: true,
      minWidth: 100,
    },
    localeText: {
      noRowsToShow: 'Add filters to query availability',
    },
    onColumnRowGroupChanged: (params) => updateSelectedRowGroupColumns(params.columnApi),
    rowGroupPanelShow: 'onlyWhenGrouping',
    onSortChanged: redrawGroupedRowsWhenSortChanged,
    suppressMakeColumnVisibleAfterUnGroup: true,
    groupDisplayType: 'custom',
    suppressAggFuncInHeader: true,
    suppressMovableColumns: true,
    alwaysShowHorizontalScroll: true,
    suppressClickEdit: true,
    suppressRowClickSelection: true,
  };

  const shouldShowPinnedBottomRow = !frameIdFilterActive && totalAvailability.length > 0 && availabilityReportData;

  const pinnedBottomRowData =
    shouldShowPinnedBottomRow && selectedMetricType
      ? [
          {
            availability: totalAvailability,
            frameId: 'Total Available',
            total: getTotalValue(availabilityReportData.summary, selectedMetricType),
          },
        ]
      : undefined;

  const handleFetchAvailabilityReportWrapper = (filters: AvailabilityReportFilter): void => {
    const transformedData = transformAvailabilityRequestPayload(filters, marketId);

    if (transformedData && selectedMetricType) {
      setCurrentFilters(filters);
      setOpenColumnGroups(new Set());
      fetchAvailabilityReport({ ...transformedData, ...defaultPayload(selectedMetricType) }, false);
    }
  };

  const clearAvailabilityReportData = (): void => {
    setAvailabilityReportData(undefined);
    setRowData([]);
    setTotalAvailability([]);
    setExpandingWeeks(new Set());
    setExpandingDays(new Set());
    setCurrentFilters(null);
    setOpenColumnGroups(new Set());
    gridApiRef.current = null;
    setGridApi(null);
    setColumnApi(null);
  };

  useEffect(() => {
    if (isRefetchingMainData) {
      return;
    }

    if (currentFilters && !isLoading) {
      const transformedData = transformAvailabilityRequestPayload(currentFilters, marketId);
      if (transformedData) {
        const hasExpandedState = openColumnGroups.size > 0;

        if (gridApi) {
          const currentFilterModel = gridApi.getFilterModel()?.['frame-id'];
          if (currentFilterModel && currentFilterModel.values && currentFilterModel.values.length > 0) {
            savedFrameIdFilterRef.current = currentFilterModel;
          }
        }

        setIsRefetchingMainData(true);
        isRefetchingMainDataRef.current = true;

        fetchAvailabilityReport({ ...transformedData, ...defaultPayload(selectedMetricType) }, hasExpandedState)
          .then(() => {
            if (openColumnGroups.size > 0) {
              needsGranularRefetchRef.current = true;
            }
            if (savedFrameIdFilterRef.current) {
              needsPinnedRowUpdateRef.current = true;
            }
          })
          .finally(() => {
            setIsRefetchingMainData(false);
            isRefetchingMainDataRef.current = false;
          });
      }
    }
  }, [selectedMetricType]);

  useEffect(() => {
    if (needsGranularRefetchRef.current && !isRefetchingMainData && !isRefetchingGranularData) {
      needsGranularRefetchRef.current = false;
      refetchExpandedGranularityData();
    }
  }, [isRefetchingMainData, isRefetchingGranularData, refetchExpandedGranularityData]);

  useEffect(() => {
    return () => {
      if (granularRefetchTimeoutRef.current) {
        clearTimeout(granularRefetchTimeoutRef.current);
        granularRefetchTimeoutRef.current = null;
      }

      gridApiRef.current = null;
      setGridApi(null);
      setColumnApi(null);
    };
  }, []);

  const exportGridAsCsv = useCallback(() => {
    gridApiRef.current?.exportDataAsCsv({
      fileName: getAvailabilityExportFileName('availability-report', 'csv'),
    });
  }, []);

  const exportGridAsExcel = useCallback(() => {
    gridApiRef.current?.exportDataAsExcel({
      fileName: getAvailabilityExportFileName('availability-report', 'xlsx'),
    });
  }, []);

  const { exportAvailabilityReport, isExporting } = useExportAvailabilityReport();

  const handleExportReport = useCallback(
    (filters: AvailabilityReportFilter) => {
      const granularity = availabilityReportData?.metadata?.granularity || AvailabilityGranularity.WEEK;
      exportAvailabilityReport(filters, selectedMetricType, granularity);
    },
    [exportAvailabilityReport, selectedMetricType, availabilityReportData?.metadata?.granularity],
  );

  return {
    availabilityReportTableParams: {
      gridOptions,
      onGridReady,
      rowData,
      pinnedBottomRowData,
    },
    availabilityReportData,
    handleChangeMetricType,
    handleFetchAvailabilityReport: handleFetchAvailabilityReportWrapper,
    clearAvailabilityReportData,
    exportGridAsCsv,
    exportGridAsExcel,
    handleExportReport,
    isLoading,
    isTableLoading,
    isExportLoading: isExporting,
    selectedMetricType,
    selectedRowGroupColumns,
    handleToggleRowGroupColumn,
    resetRowGroupColumns,
    rowGroupColumns,
  };
};
